import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type PaymentMethod = Tables<"payment_methods">;

export interface MethodDraft {
  code: string;
  name: string;
  instructions: string;
  account_reference: string;
  requires_proof: boolean;
  active: boolean;
}

export const VACIO: MethodDraft = {
  code: "",
  name: "",
  instructions: "",
  account_reference: "",
  requires_proof: true,
  active: true,
};

/**
 * Los que ya conocemos, para no obligar a nadie a inventarse un codigo.
 * `provider` se queda null: eso lo pone la integracion de la pasarela cuando
 * exista, y hasta entonces todos estos se verifican a mano.
 */
export const SUGERIDOS = [
  { code: "yape", name: "Yape", requires_proof: true, pide_numero: true },
  { code: "plin", name: "Plin", requires_proof: true, pide_numero: true },
  { code: "transfer", name: "Transferencia bancaria", requires_proof: true, pide_numero: true },
  { code: "cod", name: "Pago contra entrega", requires_proof: false, pide_numero: false },
  { code: "cash", name: "Efectivo en tienda", requires_proof: false, pide_numero: false },
] as const;

export async function fetchMethods(organizationId: string): Promise<PaymentMethod[]> {
  const { data, error } = await supabase
    .from("payment_methods")
    .select("*")
    .eq("organization_id", organizationId)
    .order("sort_order")
    .order("name");

  if (error) throw error;
  return data ?? [];
}

const limpio = (v: string): string | null => {
  const t = v.trim();
  return t === "" ? null : t;
};

export async function createMethod(
  organizationId: string,
  borrador: MethodDraft,
  sortOrder: number,
) {
  const { error } = await supabase.from("payment_methods").insert({
    organization_id: organizationId,
    code: borrador.code.trim(),
    name: borrador.name.trim(),
    instructions: limpio(borrador.instructions),
    account_reference: limpio(borrador.account_reference),
    requires_proof: borrador.requires_proof,
    active: borrador.active,
    sort_order: sortOrder,
  });

  if (error) throw error;
}

export async function updateMethod(id: string, borrador: MethodDraft) {
  // El codigo no se toca: los pedidos guardan `payment_method_code` y
  // cambiarlo dejaria pedidos apuntando a un metodo que ya no existe.
  const { error } = await supabase
    .from("payment_methods")
    .update({
      name: borrador.name.trim(),
      instructions: limpio(borrador.instructions),
      account_reference: limpio(borrador.account_reference),
      requires_proof: borrador.requires_proof,
      active: borrador.active,
    })
    .eq("id", id);

  if (error) throw error;
}

export async function toggleMethod(id: string, active: boolean) {
  const { error } = await supabase.from("payment_methods").update({ active }).eq("id", id);
  if (error) throw error;
}

export function aBorrador(fila: PaymentMethod): MethodDraft {
  return {
    code: fila.code,
    name: fila.name,
    instructions: fila.instructions ?? "",
    account_reference: fila.account_reference ?? "",
    requires_proof: Boolean(fila.requires_proof),
    active: Boolean(fila.active),
  };
}

export function validar(borrador: MethodDraft, nuevo: boolean): Record<string, string> {
  const errores: Record<string, string> = {};

  if (!borrador.name.trim()) errores.name = "Ponle un nombre visible para el comprador.";

  if (nuevo) {
    const code = borrador.code.trim();
    if (!code) errores.code = "Falta el código.";
    else if (!/^[a-z][a-z0-9_]{1,30}$/.test(code)) {
      errores.code = "Solo minúsculas, números y guion bajo. Debe empezar por letra.";
    }
  }

  // Un Yape sin numero es un metodo de pago que no se puede pagar.
  if (borrador.requires_proof && !borrador.account_reference.trim()) {
    errores.account_reference =
      "Si pides comprobante, el comprador necesita saber a qué número o cuenta pagar.";
  }

  return errores;
}
